<template>
  <div
    v-tooltip="tooltip"
    :class="['view-field-container']"
    :style="{ top, left, 'font-size': fontSize, height, width }"
  >
    <div :class="['view-field', fieldType, fieldSource, { completed }]">
      <ViewCheckboxField v-if="field.type === FieldType.Check" :value="field.value" />
      <ViewSignatureField
        v-else-if="field.type === FieldType.Signature"
        :signature-biometric="field.signatureBiometric"
        :full-name="fullName"
      />
      <ViewLinkField v-else-if="field.type === FieldType.Link" :link="field.value" />
      <template v-else>
        {{ value }}
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { FieldType } from '@getaccept/lib-shared-new/src/fields/types/enums/field-type';
import { FieldSource } from '@getaccept/lib-shared-new/src/fields/types/enums/field-source';
import type { Recipient } from '@getaccept/lib-shared-new/src/types/Recipient';
import { FieldHelper } from '@getaccept/lib-shared-new/src/fields/helpers/field.helper';
import { Placement } from '@getaccept/fried-tofu';
import type { TooltipOptions } from '@getaccept/fried-tofu';
import { initialHelper } from '@getaccept/lib-shared-new/src/helpers';
import type { Field } from '@getaccept/lib-shared-new/src/fields/types/field';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import { useI18n } from 'vue-i18n';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import ViewLinkField from './ViewLinkField.vue';
import ViewSignatureField from './ViewSignatureField.vue';
import ViewCheckboxField from './ViewCheckboxField.vue';

export default defineComponent({
  components: {
    ViewCheckboxField,
    ViewSignatureField,
    ViewLinkField,
  },
  props: {
    field: {
      type: Object as PropType<Field>,
      default: () => ({}),
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      default: () => [],
    },
    sender: {
      type: Object as PropType<User>,
      default: () => ({} as User),
    },
  },
  setup(props) {
    const { t } = useI18n();
    const fontSize = computed(() => `${(props.field.position.height * 0.6).toFixed(1)}px`);
    const top = computed(() => `${props.field.position.top}px`);
    const left = computed(() => `${props.field.position.left}px`);
    const width = computed(() => `${props.field.position.width}px`);

    const height = computed(() => {
      const dynamicHeight = [FieldType.Text, FieldType.Merge].includes(props.field.type);
      const hasValue = !!props.field.value || !!props.field.label;
      if (dynamicHeight && hasValue) {
        return 'auto';
      }
      return `${props.field.position.height}px`;
    });

    const value = computed(() => {
      if (props.field.type === FieldType.Initials) {
        return initialHelper(fullName.value);
      } else {
        return props.field.value || props.field.label || props.field.displayProperty;
      }
    });

    const fullName = computed(() => {
      switch (props.field.source) {
        case FieldSource.Recipient:
          return fieldRecipient.value?.fullName;
        case FieldSource.Sender:
          return props.sender?.fullName;
        default:
          return null;
      }
    });

    const fieldRecipient = computed(
      (): Recipient => {
        if (props.field?.recipientId) {
          return props.recipients.find(
            (recipient: Recipient) => recipient.id === props.field.recipientId
          );
        }
        return null;
      }
    );

    const completed = computed(() => FieldHelper.fieldCompleted(props.field));
    const fieldType = computed(() => `${props.field.type}-field`);

    const fieldSource = computed(() => {
      switch (props.field.source) {
        case FieldSource.Sender:
          return 'sender-field';
        case FieldSource.Recipient:
          return 'recipient-field';
        default:
          return '';
      }
    });

    const tooltip = computed(
      (): TooltipOptions => {
        if (showTooltip.value) {
          return {
            content: tooltipContent.value,
            offset: 10,
            placement: Placement.Top,
          };
        }
        return null;
      }
    );

    const showTooltip = computed(() =>
      [FieldSource.Recipient, FieldSource.Sender].includes(props.field.source)
    );

    const tooltipContent = computed(() => {
      if (props.field.source === FieldSource.Sender) {
        return t('field-assigned-to-you');
      }
      return `${t('field-assigned-to')} ${fullName.value}`;
    });

    return {
      FieldType,
      fieldType,
      fieldSource,
      tooltip,
      fontSize,
      top,
      left,
      width,
      height,
      value,
      completed,
      fullName,
    };
  },
});
</script>

<style lang="scss">
@import '../../fields/assets/colors';
</style>
<style lang="scss" scoped>
.view-field-container {
  position: absolute;
  overflow: hidden;

  .view-field {
    height: 100%;
    width: 100%;
    border-radius: var(--radius-small);
    border: 1px solid var(--base-blue);
    display: flex;
    align-items: center;
    transition: border-color 0.1s ease-in-out;
    box-sizing: border-box;
    font-weight: normal;
    background-color: var(--field-gray-background);
    color: var(--text-gray);
    border-color: var(--text-gray);
    padding-left: 0.2rem;

    &.check-field {
      padding-left: 0;
    }

    &.link-field {
      border-color: transparent;
    }

    &.text-field,
    &.merge-field {
      line-height: 1.7;
    }

    &.completed:not(.merge-field) {
      background-color: transparent;
      color: var(--text-dark);
    }

    &:hover {
      &.sender-field:not(.merge-field, .completed) {
        border-color: var(--brand-orange);
        background: var(--sender-orange-background);
        color: var(--sender-orange-placeholder);
      }

      &.recipient-field:not(.merge-field, .completed) {
        border-color: var(--recipient-green);
        background: var(--recipient-green-background);
        color: var(--recipient-green-placeholder);
      }

      &.completed {
        background: transparent;
        color: var(--text-dark);
      }

      &.merge-field {
        background-color: var(--field-gray-background);
        color: var(--text-gray);
        border-color: transparent;
      }

      &.link-field {
        border-color: var(--text-gray);
      }
    }
  }
}
</style>
